import React from "react";

import api from "./api";

import {useTenant} from "~/tenant/hooks";
import {CURRENCIES} from "~/i18n/constants";
import {Product} from "~/product/types";
import {getTotal} from "~/cart/selectors";
import {CartItem} from "~/cart/types";

interface Context {
  actions: {
    log: {
      addToCart: (product: Product) => void;
      removeFromCart: (product: Product) => void;
      viewCart: () => void;
      viewFields: () => void;
      checkout: (items: CartItem[]) => void;
      share: (product: Product, method: string) => void;
      viewProduct: (product: Product) => void;
    };
  };
}

const AnalyticsContext = React.createContext({} as Context);

const AnalyticsProvider: React.FC = ({children}) => {
  const tenant = useTenant();

  function addToCart(product: Product) {
    api.track({
      action: "add_to_cart",
      category: "checkout",
      label: product.title,
      value: 1,
    });
  }

  function removeFromCart(product: Product) {
    api.track({
      action: "remove_from_cart",
      category: "checkout",
      label: product.title,
      value: -1,
    });
  }

  function viewCart() {
    api.track({
      action: "begin_checkout",
      category: "checkout",
      label: "Overview",
      value: 1,
    });
  }

  function viewFields() {
    api.track({
      action: "checkout_progress",
      category: "checkout",
      label: "Fields",
      value: 2,
    });
  }

  function checkout(items: CartItem[]) {
    api.track({
      action: "purchase",
      category: CURRENCIES[tenant.country],
      label: tenant.id,
      value: getTotal(items),
    });
  }

  function share(product: Product, method: string) {
    api.track({
      action: "share",
      category: method,
      label: product.title,
      value: 1,
    });
  }

  function viewProduct(product: Product) {
    api.track({
      action: "view_item",
      category: "product",
      label: product?.title,
      value: 1,
    });
  }

  React.useEffect(() => {
    // Track page view
    api.pageview(window.location.pathname + window.location.search);
  }, []);

  const actions: Context["actions"] = {
    log: {
      viewCart,
      viewFields,
      addToCart,
      removeFromCart,
      checkout,
      share,
      viewProduct,
    },
  };

  return <AnalyticsContext.Provider value={{actions}}>{children}</AnalyticsContext.Provider>;
};

export {AnalyticsProvider as Provider, AnalyticsContext as default};
